// Reactive coaching: tells each chef what their buttons will do right now and
// what the most useful next step is, based on the live state of the kitchen.
import { INGREDIENTS, RECIPES, reachableRecipes } from './data.js';
import { input, soloHints } from '../engine/input.js';

const APP_NAME = { kettle: 'kettle', blender: 'blender' };
const b = (t) => `<b>${t}</b>`;
// icons for inline references (set once the icon atlas is built)
let IC = {};
export function setCoachIcons(icons) {
  IC = icons || {};
}
const icon = (key) => (IC[key] ? `<img class="ref-ic" src="${IC[key]}" alt="">` : '');
/** Recipe reference: icon + bold name. */
const rn = (r) => `<span class="ref">${icon('drink:' + r.id)}<b>${r.name}</b></span>`;
/** Ingredient reference (optionally in a prep state). */
const inm = (d, st = 'raw') => `<span class="ref">${icon('ing:' + d.id + ':' + st)}<b>${d.name}</b></span>`;
/** Guest name tag pill, same as on the order tickets. */
const gt = (guest) => `<span class="ntag" style="background:${guest.species.color}">${guest.species.name}</span>`;
/** Chef name tag pill in the chef's colour. */
const ct = (c) => `<span class="ntag" style="background:${c.color}">${c.name}</span>`;
const kb = (k) => (k.startsWith('<') ? k : `<kbd>${k}</kbd>`);

export function keysFor(chef) {
  const d = chef.controller || 'kbA';
  const hi = chef.game && chef.game.solo ? soloHints(true) : input.hintsFor(d, true);
  const first = (v) => (v.startsWith('<') ? v : v.split(' ')[0]);
  return { grab: first(hi.grab), use: first(hi.use), swap: first(hi.swap) };
}

const isSludge = (id) => id === 'sludge';
/** A kettle or jug holding a finished boba tea. */
const bobaResult = (v) => v.kind === 'vessel' && !!RECIPES[v.result] && !!RECIPES[v.result].boba;

/** What pressing Grab (and holding Use) will do on the current target. */
export function previewAction(g, chef, tgt) {
  const H = chef.held;
  const k = keysFor(chef);
  const ok = (t, use) => ({ text: `${kb(k.grab)} ${t}` + (use ? ` · ${kb(k.use)} ${use}` : ''), bad: false });
  const onlyUse = (t) => ({ text: `${kb(k.use)} ${t}`, bad: false });
  const bad = (t) => ({ text: t, bad: true });
  if (!H) {
    if (!tgt) return null;
    if (tgt.floor) return ok('Pick up');
    const s = tgt.station;
    if (s.fire) return bad('On fire! Use an extinguisher');
    switch (s.kind) {
      case 'crate':
        return ok(s.item ? 'Grab' : 'Take ' + inm(INGREDIENTS[s.ingId]));
      case 'rack':
        return s.item ? ok('Grab') : s.clean > 0 ? ok('Take clean cup') : bad('No clean cups — wash some!');
      case 'sink':
        return s.dirty > 0 ? onlyUse(chef.job === s ? 'Washing… (move to stop)' : 'Wash all') : bad('Sink is empty');
      case 'bin':
        return bad('Zapper: bring things to empty');
      case 'cups':
        return ok('Take a boba cup');
      case 'straws':
        return ok('Take a straw');
      case 'sealer':
        if (s.item && s.item.kind === 'bobacup' && !s.item.sealed) return ok('Grab (sealing…)');
        break;
      case 'bar': {
        if (s.item) return ok('Take dirty cup');
        const seat = g.world.seats.indexOf(s.x);
        const guest = g.guests.find((x) => x.seat === seat && x.state === 'wait');
        return guest ? bad(`${gt(guest)} wants ${rn(guest.recipe)}`) : null;
      }
      case 'chop':
      case 'grind': {
        const it = s.item;
        if (it && it.kind === 'ing' && it.needsPrep) {
          const want = s.kind === 'chop' ? 'chop' : 'grind';
          if (it.def.prep === want) return chef.job === s ? { text: `${want === 'chop' ? 'Chopping' : 'Grinding'}… (move to stop)`, bad: false } : ok('Grab', want === 'chop' ? 'Chop' : 'Grind');
          return bad(`Wrong station: ${inm(it.def)} needs the ${it.def.prep === 'chop' ? 'chopping board' : 'grinder'}`);
        }
        return s.item ? ok('Grab') : null;
      }
    }
    return s.item ? ok('Grab ' + itemName(s.item)) : null;
  }
  // holding something
  if (tgt && tgt.chef) return ok(`Give to ${ct(tgt.chef)}`);
  if (!tgt || tgt.floor) return g._facingSolid(chef) ? null : ok('Drop on floor');
  const s = tgt.station;
  if (s.fire) return H.kind === 'ext' ? onlyUse('Hold to spray') : bad('On fire!');
  if (H.kind === 'cup' && H.dirty && s.item && s.item.kind === 'cup' && s.item.dirty) return ok(`Stack up (${H.count + s.item.count} cups)`);
  if (H.kind === 'ext') return s.item ? null : ok('Put down');
  const strawCheck = (cup) => (cup.straw ? bad('Already has a straw') : !cup.sealed ? bad(cup.sealable ? 'Seal it first (Sealer)' : g._whyNotSealable(cup)) : ok('Stab in a straw'));
  const pearlCheck = (cup) => (cup.sealed ? bad('Already sealed') : cup.pearls ? bad('Already has pearls') : ok('Scoop in pearls'));
  switch (s.kind) {
    case 'cups':
      if (H.kind === 'bobacup') return !H.pearls && !H.drink ? ok('Put back') : bad('Already used: finish it or zap it');
      return bad('Boba cups only');
    case 'straws':
      if (H.kind === 'straw') return ok('Put back');
      return H.kind === 'bobacup' ? strawCheck(H) : bad('Straws only');
    case 'sealer':
      if (s.item) break;
      if (H.kind !== 'bobacup') return bad('Boba cups only');
      return H.sealable ? ok('Seal it') : bad(g._whyNotSealable(H));
    case 'bin':
      if (H.kind === 'bobacup') return ok(H.pearls || H.drink ? 'Zap it (boba cups are disposable)' : 'Zap the cup');
      if (H.kind === 'straw') return ok('Zap it');
      if (H.kind === 'ing') return ok('Zap it');
      if (H.kind === 'cup') return H.drink ? ok(isSludge(H.drink) ? 'Empty the sludge' : 'Empty cup') : g.justDumped(chef, s) ? { text: 'Emptied ✓', bad: false } : bad('Cups are reusable');
      if (H.kind === 'vessel') return H.contents.length || H.burnt ? ok(H.result && isSludge(H.result) ? 'Empty the sludge' : 'Empty it') : g.justDumped(chef, s) ? { text: 'Emptied ✓', bad: false } : bad('Already empty');
      return null;
    case 'bar': {
      const seat = g.world.seats.indexOf(s.x);
      const guest = g.guests.find((x) => x.seat === seat && x.state === 'wait');
      if (!guest) return bad('No guest here');
      if ((H.kind !== 'cup' && H.kind !== 'bobacup') || !H.drink) return bad(`${gt(guest)} wants ${rn(guest.recipe)} in a cup`);
      const r = guest.recipe;
      if (H.kind === 'bobacup' && H.drink === r.id) {
        if (!H.pearls) return bad('Needs boba pearls');
        if (!H.sealed) return bad('Seal it first (Sealer)');
        if (!H.straw) return bad('Needs a straw');
        return ok('Serve!');
      }
      if (H.drink === r.id && !!H.fizzy === !!r.fizz) return ok('Serve!');
      if (H.drink === r.id && r.fizz) return bad('Needs fizz first (Carbonator)');
      return bad(`Wrong drink — wants ${rn(r)}`);
    }
    case 'sink':
      return H.kind === 'cup' && H.dirty ? ok('Drop in & wash') : bad('Only dirty cups');
    case 'rack':
      if (H.kind === 'bobacup') return bad('Boba cups are disposable');
      if (H.kind === 'cup' && H.isCleanEmpty) return ok('Return cup');
      return s.item ? null : H.kind === 'cup' ? bad(H.dirty ? 'Wash it first' : 'Cup is full') : ok('Put down');
    case 'crate':
      if (H.kind === 'bobacup' && s.ingId === 'pearls') return pearlCheck(H);
      if (H.kind === 'ing' && H.id === s.ingId && H.state === 'raw') return ok('Put back');
      break;
    case 'chute':
      return null;
  }
  if (s.item) {
    const X = s.item;
    // boba
    const pourCheck = (cup, v) =>
      !v.result ? bad(v.contents.length ? 'Still brewing…' : 'Empty')
      : isSludge(v.result) ? bad('Sludge stays sludge — zap it')
      : !RECIPES[v.result].boba ? bad('Boba cups are for boba tea')
      : cup.sealed ? bad('Already sealed') : cup.drink ? bad('Cup is full')
      : ok('Pour ' + rn(RECIPES[v.result]));
    if (H.kind === 'ing' && H.id === 'pearls' && X.kind === 'bobacup') return pearlCheck(X);
    if (H.kind === 'bobacup' && X.kind === 'ing' && X.id === 'pearls') return pearlCheck(H);
    if (H.kind === 'straw' && X.kind === 'bobacup') return strawCheck(X);
    if (H.kind === 'bobacup' && X.kind === 'straw') return strawCheck(H);
    if (H.kind === 'bobacup' && X.kind === 'vessel') return pourCheck(H, X);
    if (H.kind === 'vessel' && X.kind === 'bobacup') return pourCheck(X, H);
    if ((H.kind === 'ing' && H.id === 'pearls' && X.kind === 'vessel') || (H.kind === 'vessel' && X.kind === 'ing' && X.id === 'pearls')) return bad('Pearls go in a boba cup');
    if ((H.kind === 'cup' && bobaResult(X)) || (X.kind === 'cup' && bobaResult(H))) return bad('Boba tea needs a boba cup');
    if (H.kind === 'ing' && X.kind === 'vessel') {
      if (!H.ready) return bad(`${H.def.prep === 'chop' ? 'Chop' : 'Grind'} it first!`);
      if (X.burnt) return bad('Burnt — zap it');
      if (X.result === 'sludge') return bad('Sludge stays sludge — zap it');
      if (!X.canAdd(H)) return bad('Full (max 4)');
      const cont = X.contents.concat(H.id);
      return reachableRecipes(X.app, cont).length ? ok(`Add to ${APP_NAME[X.app]}`) : { text: `${kb(k.grab)} Add (makes sludge!)`, bad: true };
    }
    if (H.kind === 'vessel' && X.kind === 'ing') {
      if (!X.ready) return bad(`${X.def.prep === 'chop' ? 'Chop' : 'Grind'} it first!`);
      if (H.result === 'sludge' || H.burnt) return bad('Sludge stays sludge — zap it');
      return H.canAdd(X) ? ok('Scoop it in') : bad('Full (max 4)');
    }
    if (H.kind === 'cup' && X.kind === 'vessel') {
      if (!H.isCleanEmpty) return bad(H.dirty ? 'Wash the cup first' : 'Cup is full');
      if (!X.result) return bad(X.contents.length ? 'Still brewing…' : 'Empty');
      return ok(isSludge(X.result) ? 'Pour sludge (yuck)' : 'Pour ' + rn(RECIPES[X.result]));
    }
    if (H.kind === 'vessel' && X.kind === 'cup') {
      if (!X.isCleanEmpty) return bad(X.dirty ? 'Wash the cup first' : 'Cup is full');
      if (!H.result) return bad(H.contents.length ? 'Still brewing…' : 'Empty');
      return ok('Pour into cup');
    }
    if (H.kind === 'cup' && X.kind === 'cup' && H.dirty && X.dirty) return ok('Stack cups');
    return bad('Occupied');
  }
  if (s.accepts(H)) {
    if (s.kind === 'carbonator') return ok('Carbonate');
    if (s.kind === 'sealer') return ok('Seal it');
    if (s.kind === 'chop' && H.kind === 'ing' && H.def.prep === 'chop' && H.needsPrep) return ok('Place & chop');
    if (s.kind === 'grind' && H.kind === 'ing' && H.def.prep === 'grind' && H.needsPrep) return ok('Place & grind');
    if (s.kind === 'stove' || s.kind === 'blender') return ok(H.contents.length ? 'Put back to keep brewing' : 'Place');
    return ok('Place');
  }
  if (s.kind === 'stove') return bad('Kettles only');
  if (s.kind === 'blender') return bad('Blender jugs only');
  if (s.kind === 'carbonator') return bad(H.kind === 'bobacup' ? 'Boba doesn’t fizz' : 'Filled cups only');
  return bad("Can't place here");
}

function itemName(it) {
  if (it.kind === 'ing') return it.def.name;
  if (it.kind === 'cup') return it.dirty ? 'dirty cup' : it.drink ? (isSludge(it.drink) ? 'sludge' : RECIPES[it.drink].name) : 'cup';
  if (it.kind === 'vessel') return it.app === 'kettle' ? 'kettle' : 'blender jug';
  if (it.kind === 'ext') return 'extinguisher';
  if (it.kind === 'bobacup') return it.drink ? RECIPES[it.drink].name : 'boba cup';
  if (it.kind === 'straw') return 'straw';
  return '';
}

/** Next step for a chef holding a boba cup: pearls → tea → Sealer → straw → serve. */
function bobaHint(chef, H, k, st, waiting, barFor, vesselSt) {
  if (H.complete) {
    const r = RECIPES[H.drink];
    const guest = waiting.find((x) => x.recipe.id === H.drink);
    if (guest) return { text: `Serve the ${rn(r)} across the bar to ${gt(guest)}, who ordered it`, target: barFor(guest) };
    return { text: `Nobody is waiting for ${rn(r)} right now. Keep it on a counter for the next guest`, target: null };
  }
  if (H.sealed) return { text: `Stab a straw in: face the ${b('straw holder')} and press ${b(k.grab)}`, target: nearest(chef, st((s) => s.kind === 'straws')) };
  if (H.sealable) return { text: `Pearls and tea are in. Set the cup in the ${b('Sealer')} to seal it`, target: nearest(chef, st((s) => s.kind === 'sealer' && !s.item)) };
  if (!H.pearls) return { text: `Scoop ${inm(INGREDIENTS.pearls)} into the cup: face the pearls crate and press ${b(k.grab)}`, target: nearest(chef, st((s) => (s.kind === 'crate' && s.ingId === 'pearls') || (s.item && s.item.kind === 'ing' && s.item.id === 'pearls'))) };
  const ready = vesselSt.find((s) => bobaResult(s.item));
  if (ready) return { text: `Face the finished ${APP_NAME[ready.item.app]} and press ${b(k.grab)} to pour the ${rn(RECIPES[ready.item.result])}`, target: ready };
  return { text: 'Pearls are in! Set the cup on a counter while the tea brews', target: null };
}

function nearest(chef, list) {
  let best = null, bd = 1e9;
  for (const s of list) {
    const d = Math.hypot(s.wx - chef.x, s.wz - chef.z);
    if (d < bd) { bd = d; best = s; }
  }
  return best;
}

function missingFor(recipe, contents) {
  const need = recipe.ing.slice();
  for (const c of contents) {
    const i = need.indexOf(c);
    if (i < 0) return null; // contents don't fit this recipe
    need.splice(i, 1);
  }
  return need;
}

/** The single most useful next step for this chef: {text, target}. */
export function coachHint(g, chef) {
  const H = chef.held;
  const k = keysFor(chef);
  const st = (pred) => g.stations.filter(pred);
  const bins = st((s) => s.kind === 'bin');
  const waiting = g.guests.filter((x) => x.state === 'wait').sort((a, c) => a.patience - c.patience);
  const barFor = (guest) => g.stations.find((s) => s.kind === 'bar' && s.x === g.world.seats[guest.seat]);
  const vesselSt = st((s) => s.item && s.item.kind === 'vessel');
  const fires = st((s) => s.fire);

  if (chef.job) {
    const what = chef.job.kind === 'sink' ? 'washing up' : chef.job.kind === 'chop' ? 'chopping' : 'grinding';
    return { text: `Keep ${what}… moving away stops it (progress is saved)`, target: null };
  }
  const bobaCups = st((s) => s.item && s.item.kind === 'bobacup');
  const cupStacks = st((s) => s.kind === 'cups');
  if (H) {
    if (H.kind === 'bobacup') return bobaHint(chef, H, k, st, waiting, barFor, vesselSt);
    if (H.kind === 'straw') {
      const cups = bobaCups.filter((s) => s.item.sealed && !s.item.straw);
      if (cups.length) return { text: `Stab the straw into the sealed ${b('boba cup')}: face it and press ${b(k.grab)}`, target: nearest(chef, cups) };
      return { text: `Straws go into ${b('sealed')} boba cups. Hold on to it until a cup comes out of the ${b('Sealer')}, or put it back`, target: nearest(chef, st((s) => s.kind === 'sealer')) };
    }
    if (H.kind === 'ext') {
      if (fires.length) return { text: `Face the fire and hold ${b(k.use)} to spray foam`, target: nearest(chef, fires) };
      return { text: 'All fires are out. Put the extinguisher back on its red counter', target: nearest(chef, st((s) => s.kind === 'ext' && !s.item)) };
    }
    if (H.kind === 'cup') {
      if (H.dirty) return { text: `Dirty cups go in the ${b('sink')}; your chef starts washing straight away while standing there`, target: nearest(chef, g.sinks) };
      if (H.drink && isSludge(H.drink))
        return { text: `That's ${b('Murky Sludge')} (the wrong mix of ingredients). No guest will drink it: empty the cup at the ${b('Zapper')} (the crackling tesla coils)`, target: nearest(chef, bins) };
      if (H.drink) {
        const r = RECIPES[H.drink];
        const guest = waiting.find((x) => x.recipe.id === H.drink);
        if (r.fizz && !H.fizzy) return { text: `${rn(r)} must be fizzy: set it in the ${b('Carbonator')} for a moment`, target: nearest(chef, st((s) => s.kind === 'carbonator' && !s.item)) };
        if (guest) return { text: `Serve the ${rn(r)} across the bar to ${gt(guest)}, who ordered it`, target: barFor(guest) };
        return { text: `Nobody is waiting for ${rn(r)} right now. Keep it on a counter for the next guest, or empty it at the Zapper`, target: null };
      }
      const ready = vesselSt.find((s) => s.item.result && !isSludge(s.item.result) && !bobaResult(s.item));
      if (ready) return { text: `Face the finished ${APP_NAME[ready.item.app]} and press ${b(k.grab)} to pour into the cup`, target: ready };
      return { text: 'Hold the cup until a kettle or blender finishes, or set it on a counter', target: null };
    }
    if (H.kind === 'vessel') {
      const name = APP_NAME[H.app];
      if (H.burnt || (H.result && isSludge(H.result)))
        return { text: `${H.burnt ? 'Burnt!' : 'Murky Sludge!'} The ${name} holds the wrong mix. Carry it to the ${b('Zapper')} to empty it, then start again`, target: nearest(chef, bins) };
      if (bobaResult(H)) {
        const cups = bobaCups.filter((s) => s.item.canPour);
        if (cups.length) return { text: `Pour the ${rn(RECIPES[H.result])} into the boba cup`, target: nearest(chef, cups) };
        return { text: `${rn(RECIPES[H.result])} goes in a ${b('boba cup')}: set the ${name} down and fetch a cup from the ${b('cup stack')}`, target: nearest(chef, cupStacks) };
      }
      if (H.result) return { text: `Set the ${name} down next to a clean cup and pour, or put it back and bring a cup to it`, target: nearest(chef, g.racks) };
      const base = st((s) => (H.app === 'kettle' ? s.kind === 'stove' : s.kind === 'blender') && !s.item);
      if (H.contents.length) return { text: `Put the ${name} back on its ${H.app === 'kettle' ? 'stove' : 'blender base'} so it keeps ${H.app === 'kettle' ? 'brewing' : 'blending'}`, target: nearest(chef, base) };
      return { text: `Place the ${name} on a ${H.app === 'kettle' ? 'stove' : 'blender base'}, then add ingredients`, target: nearest(chef, base) };
    }
    if (H.kind === 'ing') {
      const d = H.def;
      if (H.id === 'pearls') {
        const cups = bobaCups.filter((s) => s.item.canTakePearls);
        if (cups.length) return { text: `Drop the ${inm(d)} into the boba cup`, target: nearest(chef, cups) };
        return { text: `${inm(d)} go in a ${b('boba cup')}. Set these down and grab a cup from the ${b('cup stack')} (with a cup in hand you can also scoop pearls straight from the crate)`, target: nearest(chef, cupStacks) };
      }
      if (H.needsPrep) {
        const kind = d.prep === 'chop' ? 'chop' : 'grind';
        const where = st((s) => s.kind === kind && !s.item && !s.fire);
        return { text: `${inm(d)} must be ${d.prep === 'chop' ? 'chopped' : 'ground'}: place it on the ${b(kind === 'chop' ? 'chopping board' : 'grinder')} and stay put (it starts automatically)`, target: nearest(chef, where) };
      }
      const guest = waiting.find((x) => x.recipe.ing.includes(H.id));
      if (guest) {
        const r = guest.recipe;
        const fits = vesselSt.filter((s) => s.item.app === r.app && !s.item.result && missingFor(r, s.item.contents) && missingFor(r, s.item.contents).includes(H.id));
        const empty = vesselSt.filter((s) => s.item.app === r.app && !s.item.contents.length);
        const tgt = nearest(chef, fits.length ? fits : empty);
        return { text: `${rn(r)} is made in a ${b(APP_NAME[r.app])}: drop the ${inm(d, H.state)} in`, target: tgt };
      }
      return { text: `No order needs ${inm(d, H.state)} right now. Keep it on a counter, or zap it`, target: nearest(chef, bins) };
    }
  }
  // empty hands
  if (fires.length) {
    const ext = st((s) => s.item && s.item.kind === 'ext' && !s.fire);
    if (ext.length) return { text: `${b('Fire!')} Grab the Cryo Extinguisher${ext.some((s) => s.kind === 'ext') ? ' from its red counter' : ''} and spray the flames`, target: nearest(chef, ext) };
    const onFloor = g.floorItems.find((f) => f.item.kind === 'ext');
    if (onFloor) return { text: `${b('Fire!')} The extinguisher is on the floor here. Grab it and spray the flames`, target: { wx: onFloor.x, wz: onFloor.z, node: { position: [0, 0, 0] } } };
    const holder = g.chefs.find((c) => c !== chef && c.held && c.held.kind === 'ext');
    if (holder) {
      const how = holder.isBot ? 'Your bot helper is on it' : 'They should face the fire and hold spray';
      return { text: `${b('Fire!')} ${ct(holder)} has the extinguisher. ${how}`, target: { wx: holder.x, wz: holder.z, node: { position: [0, 0.7, 0] } } };
    }
    return { text: `${b('Fire!')} Find the extinguisher and spray the flames`, target: nearest(chef, fires) };
  }
  // only a kettle still on its stove can boil over (off the heat it just waits, safe)
  const hot = vesselSt.find((s) => s.kind === 'stove' && s.item.app === 'kettle' && s.item.result && s.item.over > 4 && !s.item.burnt);
  if (hot) return { text: `A kettle is ${b('boiling over')}! Pour it into a cup or take it off the stove before it catches fire`, target: hot };
  const wait = vesselSt.find((s) => s.item.waiting);
  if (wait) {
    const miss = wait.item.missing;
    const names = [...new Set(miss.flatMap((r) => r.missing))].map((id) => inm(INGREDIENTS[id], INGREDIENTS[id].prep === 'chop' ? 'chopped' : INGREDIENTS[id].prep === 'grind' ? 'ground' : 'raw')).slice(0, 3).join(' or ');
    return { text: `The ${APP_NAME[wait.item.app]} is missing an ingredient: add ${names} to finish the drink`, target: wait };
  }
  const sludge = vesselSt.find((s) => s.item.burnt || s.item.result === 'sludge');
  if (sludge) return { text: `The ${APP_NAME[sludge.item.app]} made ${b('Murky Sludge')} (wrong ingredients). Pick it up and empty it at the ${b('Zapper')}`, target: sludge };
  const prep = st((s) => (s.kind === 'chop' || s.kind === 'grind') && s.item && s.item.kind === 'ing' && s.item.needsPrep && s.item.def.prep === (s.kind === 'chop' ? 'chop' : 'grind'));
  if (prep.length) return { text: `Press ${b(k.use)} at the ${prep[0].kind === 'chop' ? 'chopping board' : 'grinder'} to finish the ${inm(prep[0].item.def)}`, target: nearest(chef, prep) };
  // boba cups waiting for their next step
  const needStraw = bobaCups.filter((s) => s.item.sealed && !s.item.straw);
  if (needStraw.length) return { text: `A boba cup is sealed! Grab a straw from the ${b('straw holder')} and stab it in`, target: nearest(chef, st((s) => s.kind === 'straws')) };
  const toSeal = bobaCups.filter((s) => s.kind !== 'sealer' && s.item.sealable);
  if (toSeal.length) return { text: `Pearls and tea are in. Put the boba cup in the ${b('Sealer')}`, target: nearest(chef, toSeal) };
  const sealing = bobaCups.find((s) => s.kind === 'sealer' && s.item.sealable);
  if (sealing && !g.chefs.some((c) => c.held && c.held.kind === 'straw'))
    return { text: `The ${b('Sealer')} is sealing the ${rn(RECIPES[sealing.item.drink])}. Grab a straw so it's ready to go`, target: nearest(chef, st((s) => s.kind === 'straws')) };
  const readyCup = g.stations.find((s) => s.item && (s.item.kind === 'cup' || (s.item.kind === 'bobacup' && s.item.complete)) && s.item.drink && !isSludge(s.item.drink) && waiting.some((x) => x.recipe.id === s.item.drink && (!x.recipe.fizz || s.item.fizzy)));
  if (readyCup) return { text: `A finished ${rn(RECIPES[readyCup.item.drink])} is waiting. Pick it up and serve it!`, target: readyCup };
  const ready = vesselSt.find((s) => s.item.result && !isSludge(s.item.result));
  const cleanCups = g.racks.reduce((a, r) => a + r.clean, 0);
  // a kettle that's been taken off the stove can't boil over: say so rather than hurry anyone
  const safe = ready && ready.item.app === 'kettle' && ready.kind !== 'stove' ? ' (safe off the heat)' : '';
  if (ready && bobaResult(ready.item)) {
    const cup = bobaCups.find((s) => s.item.canPour);
    if (cup) return { text: `${rn(RECIPES[ready.item.result])} is ready${safe}! Pick up the boba cup and pour it in`, target: cup };
    return { text: `${rn(RECIPES[ready.item.result])} is ready${safe}! Grab a boba cup from the ${b('cup stack')} and pour it in`, target: nearest(chef, cupStacks) };
  }
  if (ready && cleanCups) return { text: `${rn(RECIPES[ready.item.result])} is ready${safe}! Grab a clean cup from the rack, then pour`, target: nearest(chef, g.racks.filter((r) => r.clean)) };
  const dirtyBar = st((s) => s.kind === 'bar' && s.item && s.item.dirty);
  const sinkDirty = g.sinks.filter((s) => s.dirty > 0);
  if (!cleanCups && sinkDirty.length) return { text: `Out of clean cups! Press ${b(k.use)} at the sink to wash`, target: nearest(chef, sinkDirty) };
  if (dirtyBar.length && (!cleanCups || cleanCups < 2)) return { text: 'Guests left dirty cups on the bar. Take them to the sink', target: nearest(chef, dirtyBar) };
  if (waiting.length) {
    // boba teas already poured (on counters, in the Sealer or in hand) are spoken for
    const bobaLeft = {};
    for (const it of [...bobaCups.map((s) => s.item), ...g.chefs.map((c) => c.held).filter((h) => h && h.kind === 'bobacup')]) if (it.drink) bobaLeft[it.drink] = (bobaLeft[it.drink] || 0) + 1;
    for (const guest of waiting) {
      const r = guest.recipe;
      if (r.boba && bobaLeft[r.id] > 0) {
        bobaLeft[r.id]--;
        continue;
      }
      // find a vessel already working on it
      const busy = vesselSt.find((s) => s.item.app === r.app && !s.item.result && s.item.contents.length && missingFor(r, s.item.contents));
      const miss = busy ? missingFor(r, busy.item.contents) : r.ing;
      if (!miss.length) continue; // brewing, nothing to add
      const id = miss[0];
      const crate = st((s) => s.kind === 'crate' && s.ingId === id);
      const belt = st((s) => s.kind === 'conveyor' && s.item && s.item.kind === 'ing' && s.item.id === id);
      const src = nearest(chef, crate.length ? crate : belt);
      const d = INGREDIENTS[id];
      const how = d.prep ? ` (then ${d.prep} it)` : '';
      return { text: `${gt(guest)} wants ${rn(r)}: grab ${inm(d)}${how} for the ${APP_NAME[r.app]}${r.fizz ? ', then fizz it' : ''}${r.boba ? ', then pour it over pearls in a boba cup' : ''}`, target: src || null };
    }
    if (waiting.some((x) => x.recipe.boba)) {
      const held = g.chefs.some((c) => c.held && c.held.kind === 'bobacup' && c.held.pearls && c.held.canPour);
      if (!held && !bobaCups.some((s) => s.item.pearls && s.item.canPour))
        return { text: `Tea is brewing. Get a ${b('boba cup')} ready: take one from the cup stack and scoop in ${inm(INGREDIENTS.pearls)}`, target: nearest(chef, cupStacks) };
    }
    if (dirtyBar.length) return { text: 'Drinks are brewing. Meanwhile, clear dirty cups from the bar', target: nearest(chef, dirtyBar) };
    return { text: 'Drinks are brewing. Get a clean cup ready!', target: nearest(chef, g.racks) };
  }
  if (dirtyBar.length) return { text: 'Clear the dirty cups from the bar and wash them', target: nearest(chef, dirtyBar) };
  return { text: 'Waiting for guests… new orders appear above each guest', target: null };
}
